/**
 * Container and metric primitives for dense demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId, useState } from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";
import { focusRingStyle, MONO_FONT } from "./styles.js";

export interface DemoPanelCardProps {
  readonly title: string;
  readonly description?: string;
  readonly actions?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly bodyClassName?: string;
}

export function DemoPanelCard({
  title,
  description,
  actions,
  footer,
  children,
  bodyClassName,
}: DemoPanelCardProps): React.JSX.Element {
  return (
    <section
      aria-label={title}
      style={{
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        padding: 20,
      }}
    >
      <header
        style={{
          alignItems: "flex-start",
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ color: GRAY[900], fontSize: 14, fontWeight: 700, margin: 0 }}>{title}</h2>
          {description !== undefined && (
            <p style={{ color: GRAY[600], fontSize: 12, margin: "0.25rem 0 0" }}>{description}</p>
          )}
        </div>
        {actions !== undefined && (
          <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 8 }}>
            {actions}
          </div>
        )}
      </header>
      <div className={bodyClassName} style={{ color: GRAY[700], fontSize: 14, marginTop: 12 }}>
        {children}
      </div>
      {footer !== undefined && (
        <footer
          style={{
            borderTop: `1px solid ${GRAY[300]}`,
            color: GRAY[600],
            fontSize: 12,
            marginTop: 16,
            paddingTop: 12,
          }}
        >
          {footer}
        </footer>
      )}
    </section>
  );
}

export interface DemoCollapsibleProps {
  readonly title: string;
  readonly description?: string;
  readonly defaultOpen?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly children: React.ReactNode;
}

export function DemoCollapsible({
  title,
  description,
  defaultOpen = false,
  onOpenChange,
  children,
}: DemoCollapsibleProps): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const baseId = useId();
  const buttonId = `${baseId}-trigger`;
  const panelId = `${baseId}-panel`;
  return (
    <section
      style={{
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        id={buttonId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            onOpenChange?.(next);
            return next;
          });
        }}
        style={{
          ...focusRingStyle(),
          alignItems: "center",
          background: "transparent",
          border: 0,
          color: GRAY[900],
          cursor: "pointer",
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          padding: "0.75rem 1rem",
          textAlign: "left",
          width: "100%",
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 700 }}>{title}</span>
          {description !== undefined && (
            <span style={{ color: GRAY[600], display: "block", fontSize: 12, marginTop: 2 }}>
              {description}
            </span>
          )}
        </span>
        <span aria-hidden="true" style={{ color: GRAY[500], fontFamily: MONO_FONT }}>
          {open ? "-" : "+"}
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        style={open ? { borderTop: `1px solid ${GRAY[300]}`, padding: "0.75rem 1rem" } : undefined}
      >
        {open ? children : null}
      </div>
    </section>
  );
}

export type DemoStatTone = "default" | "ok" | "warn" | "danger" | "seam";

export interface DemoStatProps {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly unit?: string;
  readonly hint?: string;
  readonly tone?: DemoStatTone;
  readonly mono?: boolean;
}

const STAT_TONE: Readonly<Record<DemoStatTone, string>> = {
  default: GRAY[900],
  ok: SAFE[700],
  warn: "#b45309",
  danger: WARN[700],
  seam: PRIMARY[700],
};

export function DemoStat({
  label,
  value,
  unit,
  hint,
  tone = "default",
  mono = false,
}: DemoStatProps): React.JSX.Element {
  return (
    <div
      style={{
        background: GRAY[100],
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div
        style={{
          color: GRAY[500],
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: STAT_TONE[tone],
          fontFamily: mono ? MONO_FONT : undefined,
          fontSize: 18,
          fontWeight: 700,
          marginTop: 4,
        }}
      >
        {value}
        {unit !== undefined && (
          <span style={{ color: GRAY[500], fontSize: 12, fontWeight: 400, marginLeft: 4 }}>
            {unit}
          </span>
        )}
      </div>
      {hint !== undefined && (
        <div style={{ color: GRAY[500], fontSize: 11, marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

export function DemoStatGrid({
  children,
  columns = 3,
}: {
  readonly children: React.ReactNode;
  readonly columns?: 2 | 3 | 4;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gap: 12,
        gridTemplateColumns: `repeat(auto-fit, minmax(${columns === 4 ? "8rem" : "10rem"}, 1fr))`,
      }}
    >
      {children}
    </div>
  );
}
